import React, { useMemo, useEffect, useRef } from "react";
import cn from "classnames";
import Tooltip from "@material-ui/core/Tooltip";
import useTooltipStyles from "./style";
import SelectTextWidthIcon from "./Primitives/icons/SelectTextWidthIcon";
import TextAutosizeIcon from "./Primitives/icons/TextAutosizeIcon";
import FixedSizeIcon from "./Primitives/icons/FixedSizeIcon";

interface TextBoxComponentProps {
  selectedShape: any;
  editor: any;
  selectedShapes: any[];
}

type SizeMode = "auto" | "fixed-width" | "fixed";

const TextBoxComponent = ({ selectedShape, editor, selectedShapes }: TextBoxComponentProps) => {
  const [sizeMode, setSizeMode] = React.useState<SizeMode>(() => {
    if (!selectedShape) return "auto";
    const { autoSize, fixedHeight } = selectedShape.props;
    if (autoSize) return "auto";
    if (fixedHeight) return "fixed";
    return "fixed-width";
  });

  const classes = useTooltipStyles();
  const isManuallyChanging = useRef(false);

  // Sync local state with selected shape
  useEffect(() => {
    if (isManuallyChanging.current) {
      setTimeout(() => {
        isManuallyChanging.current = false;
      }, 100);
      return;
    }

    if (selectedShape) {
      const { autoSize, fixedHeight } = selectedShape.props;
      if (autoSize) {
        setSizeMode("auto");
      } else if (fixedHeight) {
        setSizeMode("fixed");
      } else {
        setSizeMode("fixed-width");
      }
    }
  }, [selectedShape?.props?.autoSize, selectedShape?.props?.fixedHeight]);

  const itemClassName = useMemo(
    () =>
      cn(
        "w-7 h-7 flex justify-center items-center cursor-pointer transition-all px-3 py-1 rounded-[4px] hover:bg-[#E2E2FC]/50"
      ),
    []
  );

  const modes = useMemo(
    () => [
      { label: "Auto size", icon: <TextAutosizeIcon />, mode: "auto" as SizeMode, selected: sizeMode === "auto" },
      {
        label: "Fixed width",
        icon: <SelectTextWidthIcon />,
        mode: "fixed-width" as SizeMode,
        selected: sizeMode === "fixed-width",
      },
      { label: "Fixed size", icon: <FixedSizeIcon />, mode: "fixed" as SizeMode, selected: sizeMode === "fixed" },
    ],
    [sizeMode]
  );

  const handleModeChange = (mode: SizeMode) => {
    const textShapes =
      selectedShapes.length > 1
        ? selectedShapes.filter((shape: any) => shape.type === "customText")
        : selectedShape && selectedShape.type === "customText"
        ? [selectedShape]
        : [];

    if (textShapes.length === 0) return;

    // Check if all shapes already have this mode
    const allAlreadyInMode = textShapes.every((shape: any) => {
      const { autoSize, fixedHeight } = shape.props;
      if (mode === "auto") return autoSize === true;
      if (mode === "fixed") return autoSize === false && fixedHeight === true;
      return autoSize === false && fixedHeight === false;
    });

    if (allAlreadyInMode) return;

    isManuallyChanging.current = true;
    setSizeMode(mode);

    const textShapeIds: string[] = [];

    textShapes.forEach((shape: any) => {
      textShapeIds.push(shape.id);
      const geometry = editor.getShapeGeometry(shape);
      const currentVisualWidth = geometry.bounds.width / (shape.props.scale || 1);
      const currentVisualHeight = geometry.bounds.height / (shape.props.scale || 1);

      let updateProps: any;

      switch (mode) {
        case "auto":
          updateProps = {
            autoSize: true,
            fixedHeight: false,
          };
          break;
        case "fixed-width":
          updateProps = {
            w: currentVisualWidth,
            autoSize: false,
            fixedHeight: false,
          };
          break;
        case "fixed":
          updateProps = {
            w: currentVisualWidth,
            h: currentVisualHeight,
            autoSize: false,
            fixedHeight: true,
            verticalAlign: "start", // Set to top by default
          };
          break;
      }

      editor.updateShape({
        ...shape,
        isLocked: false,
        props: {
          ...shape.props,
          ...updateProps,
        },
      });
    });

    // Deselect and re-select to force proper recalculation (only for auto mode)
    if (mode === "auto" && textShapeIds.length > 0) {
      setTimeout(() => {
        editor.setSelectedShapes([]);
        setTimeout(() => {
          editor.setSelectedShapes(textShapeIds);
        }, 10);
      }, 50);
    }
  };

  return (
    <div
      className="flex items-center"
      style={{
        border: "1px solid #E4E4EA",
        borderRadius: "4px",
        width: "100%",
      }}
    >
      {modes.map((item, index) => (
        <Tooltip key={item.label} title={item.label} arrow classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}>
          <div
            onClick={() => handleModeChange(item.mode)}
            style={{
              flex: 1,
              padding: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRight: index < modes.length - 1 ? "1px solid #E4E4EA" : "none",
              backgroundColor: item.selected ? "#E2E2FC" : "transparent",
            }}
            className="hover:!bg-[#E2E2FC] transition-colors"
          >
            <div
              style={{ width: "16px", height: "16px", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              {item.icon}
            </div>
          </div>
        </Tooltip>
      ))}
    </div>
  );
};

export default React.memo(TextBoxComponent);
